// ============================================
// AlpenScreen AI Assistant - Enhanced Version
// File: frontend/src/components/ContentStudio/AIAssistantEnhanced.js
// With History, Templates, Batch Generation & Template Library
// ============================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Wand2,
  Lightbulb,
  RefreshCw,
  Loader,
  Check,
  X,
  ChevronDown,
  AlertCircle,
  Copy,
  Star,
  History,
  Trash2,
  BookOpen,
  Layers
} from 'lucide-react';
import { 
  getAIPreferences,
  getContentHistory,
  addToHistory,
  toggleHistoryFavorite,
  deleteHistoryItem,
  copyToClipboard,
  countCharacters,
  getOptimalLength
} from '../../utils/aiPreferences';
import { 
  getTemplatesForIndustry,
  getToneById,
  parseTemplateVariables,
  fillTemplate
} from '../../utils/promptTemplates';
import TemplateTab from './AIAssistant/TemplateTab';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AIAssistantEnhanced = ({ onContentGenerated, currentText }) => {
  const { t } = useTranslation();
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' | 'optimize' | 'templates' | 'history'
  const [aiAvailable, setAiAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [batchResults, setBatchResults] = useState([]);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);

  // Preferences
  const [preferences, setPreferences] = useState(getAIPreferences());
  const [history, setHistory] = useState(getContentHistory());

  // Form state
  const [contentType, setContentType] = useState('headline');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState(preferences.defaultTone);
  const [optimizationGoal, setOptimizationGoal] = useState('clarity');
  const [batchMode, setBatchMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});

  // Check AI availability on mount
  useEffect(() => {
    checkAIStatus();
  }, []);

  // Reload history when tab changes
  useEffect(() => {
    if (activeTab === 'history') {
      setHistory(getContentHistory());
    }
  }, [activeTab]);

  // Update preferences when changed externally
  useEffect(() => {
    const interval = setInterval(() => {
      setPreferences(getAIPreferences());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const checkAIStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/ai/status`);
      const data = await response.json();
      setAiAvailable(data.configured && data.available);
    } catch (error) {
      console.error('Error checking AI status:', error);
      setAiAvailable(false);
    }
  };

  // Generate content (single or batch)
  const handleGenerate = async () => {
    if (!topic.trim() && !selectedTemplate) {
      setError(t('ai.errors.topicRequired', 'Bitte gib ein Thema ein'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setBatchResults([]);

    const finalTopic = selectedTemplate 
      ? fillTemplate(selectedTemplate.template, templateVariables)
      : topic;

    try {
      const count = batchMode ? preferences.batchCount : 1;
      const promises = [];

      for (let i = 0; i < count; i++) {
        promises.push(
          fetch(`${API_URL}/ai/generate-content`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contentType,
              topic: finalTopic,
              industry: preferences.industry,
              tone,
              language: preferences.language,
              maxLength: contentType === 'headline' ? 60 : 200
            })
          }).then(res => res.json())
        );
      }

      const results = await Promise.all(promises);

      if (batchMode) {
        setBatchResults(results.map(r => r.content));
      } else {
        setResult(results[0]);
        
        // Add to history
        addToHistory({
          type: 'generation',
          content: results[0].content,
          prompt: { 
            topic: finalTopic, 
            tone, 
            contentType,
            industry: preferences.industry 
          }
        });
      }

    } catch (err) {
      setError(t('ai.errors.generationFailed', 'Content-Generierung fehlgeschlagen'));
      console.error('Generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Optimize text
  const handleOptimize = async () => {
    if (!currentText?.trim()) {
      setError(t('ai.errors.noTextSelected', 'Kein Text ausgewählt'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/ai/optimize-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: currentText,
          goal: optimizationGoal,
          language: preferences.language,
          context: preferences.industry
        })
      });

      if (!response.ok) {
        throw new Error('Optimization failed');
      }

      const data = await response.json();
      setResult(data);

      // Add to history
      addToHistory({
        type: 'optimization',
        content: data.optimized,
        original: currentText,
        goal: optimizationGoal
      });

    } catch (err) {
      setError(t('ai.errors.optimizationFailed', 'Text-Optimierung fehlgeschlagen'));
      console.error('Optimization error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Apply single result
  const handleApply = () => {
    if (!result) return;

    const content = result.content || result.optimized;
    if (content && onContentGenerated) {
      onContentGenerated(content);
      setResult(null);
    }
  };

  // Apply batch result
  const handleApplyBatch = (content) => {
    if (content && onContentGenerated) {
      onContentGenerated(content);
      
      // Add to history
      addToHistory({
        type: 'generation',
        content: content,
        prompt: { topic, tone, contentType, industry: preferences.industry }
      });
    }
  };

  // Copy to clipboard
  const handleCopy = async (text) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopySuccess(text);
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  // Toggle favorite
  const handleToggleFavorite = (id) => {
    toggleHistoryFavorite(id);
    setHistory(getContentHistory());
  };

  // Delete history item
  const handleDeleteHistoryItem = (id) => {
    deleteHistoryItem(id);
    setHistory(getContentHistory());
  };

  // Apply from history
  const handleApplyFromHistory = (content) => {
    if (onContentGenerated) {
      onContentGenerated(content);
    }
  };

  // Select template (old system - for backward compatibility)
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setContentType(template.contentType);
    setTone(template.tone);
    
    // Parse variables
    const vars = parseTemplateVariables(template.template);
    const varObj = {};
    vars.forEach(v => { varObj[v] = ''; });
    setTemplateVariables(varObj);
    
    setActiveTab('generate');
  };

  // ❌ DEAKTIVIERT - Template Library (Option C)
  /*
  // Handle Template Library selection (NEW)
  const handleTemplateLibrarySelect = (template) => {
    console.log('Template Library selected:', template);
    
    // Apply template text directly to canvas/content
    if (template.text && onContentGenerated) {
      onContentGenerated(template.text);
      
      // Add to history
      addToHistory({
        type: 'template',
        content: template.text,
        prompt: { 
          templateName: template.name,
          templateId: template.id
        }
      });
      
      // Show success message
      setResult({ content: template.text });
      setTimeout(() => setResult(null), 3000);
    }
  };
  */

  // Get character count and optimal length
  const charCount = countCharacters(result?.content || result?.optimized || topic);
  const optimalLength = getOptimalLength(contentType);

  const getCharCountColor = () => {
    if (charCount < optimalLength.min) return 'text-orange-500';
    if (charCount > optimalLength.max) return 'text-red-500';
    if (charCount >= optimalLength.optimal - 10 && charCount <= optimalLength.optimal + 10) return 'text-green-500';
    return 'text-blue-500';
  };

  // Get templates for current industry (old system)
  const templates = getTemplatesForIndustry(preferences.industry);

  if (!aiAvailable) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('ai.notConfigured', 'KI-Assistent nicht konfiguriert')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {t('ai.configureHint', 'CLAUDE_API_KEY in Umgebungsvariablen setzen')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-4 transition-shadow hover:shadow-md">
      {/* Header - Modern & Spacious */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/10 dark:hover:to-pink-900/10 transition-all duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Sparkles size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('ai.assistant', 'KI-Assistent')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {t('ai.subtitle', 'Powered by Claude')}
            </p>
          </div>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Content */}
      {isOpen && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          {/* Tabs - Modern Pills */}
          <div className="flex gap-1 p-2 bg-gray-50 dark:bg-gray-900/50">
            <button
              onClick={() => setActiveTab('generate')}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'generate'
                  ? 'bg-white dark:bg-gray-800 text-purple-600 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-800/50'
              }`}
            >
              <Wand2 size={14} className="inline mr-1.5" />
              {t('ai.generate', 'Generieren')}
            </button>
            <button
              onClick={() => setActiveTab('optimize')}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'optimize'
                  ? 'bg-white dark:bg-gray-800 text-purple-600 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-800/50'
              }`}
            >
              <RefreshCw size={14} className="inline mr-1.5" />
              {t('ai.optimize', 'Optimieren')}
            </button>
            {/* ❌ DEAKTIVIERT - Template Library (Option C: Backend bleibt aktiv, Frontend hidden)
            <button
              onClick={() => setActiveTab('templates')}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'templates'
                  ? 'bg-white dark:bg-gray-800 text-purple-600 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-800/50'
              }`}
            >
              <BookOpen size={14} className="inline mr-1.5" />
              {t('ai.templates', 'Vorlagen')}
            </button>
            */}
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'history'
                  ? 'bg-white dark:bg-gray-800 text-purple-600 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-800/50'
              }`}
            >
              <History size={14} className="inline mr-1.5" />
              {t('ai.history', 'Verlauf')}
            </button>
          </div>

          <div className="p-4">
            {/* Generate Tab */}
            {activeTab === 'generate' && (
              <div className="space-y-3">
                {selectedTemplate && (
                  <div className="p-3 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-purple-700 dark:text-purple-400">
                        {selectedTemplate.icon} {selectedTemplate.name}
                      </p>
                      <button
                        onClick={() => setSelectedTemplate(null)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {Object.keys(templateVariables).map(varName => (
                      <input
                        key={varName}
                        type="text"
                        placeholder={varName}
                        value={templateVariables[varName]}
                        onChange={(e) => setTemplateVariables({ ...templateVariables, [varName]: e.target.value })}
                        className="w-full px-2 py-1 mb-1 text-xs border border-purple-200 dark:border-purple-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      />
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('ai.contentType', 'Typ')}
                    </label>
                    <select
                      value={contentType}
                      onChange={(e) => setContentType(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    >
                      <option value="headline">{t('ai.types.headline', 'Überschrift')}</option>
                      <option value="description">{t('ai.types.description', 'Beschreibung')}</option>
                      <option value="cta">{t('ai.types.cta', 'Call-to-Action')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('ai.tone', 'Tonalität')}
                    </label>
                    <select
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    >
                      <option value="professional">{t('ai.tones.professional', 'Professionell')}</option>
                      <option value="casual">{t('ai.tones.casual', 'Locker')}</option>
                      <option value="energetic">{t('ai.tones.energetic', 'Energiegeladen')}</option>
                      <option value="elegant">{t('ai.tones.elegant', 'Elegant')}</option>
                    </select>
                  </div>
                </div>

                {!selectedTemplate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('ai.topic', 'Thema')}
                    </label>
                    <input
                      type="text"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder={t('ai.topicPlaceholder', 'z.B. Sommer-Aktion, Neues Menü...')}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    />
                    {topic && (
                      <p className={`text-xs mt-2 font-medium ${getCharCountColor()}`}>
                        {charCount} / {optimalLength.optimal} {t('ai.characters', 'Zeichen')}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <input
                    type="checkbox"
                    id="batchMode"
                    checked={batchMode}
                    onChange={(e) => setBatchMode(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <label htmlFor="batchMode" className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2 cursor-pointer">
                    <Layers size={16} />
                    {preferences.batchCount} {t('ai.variants', 'Varianten')} generieren
                  </label>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {loading ? <Loader size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {loading ? t('ai.generating', 'Generiere...') : t('ai.generate', 'Generieren')}
                </button>
              </div>
            )}

            {/* Optimize Tab */}
            {activeTab === 'optimize' && (
              <div className="space-y-3">
                <div className="p-3 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl border border-blue-200 dark:border-blue-800 shadow-sm">
                  <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                    {currentText 
                      ? `"${currentText.substring(0, 50)}${currentText.length > 50 ? '...' : ''}"`
                      : t('ai.noTextSelected', 'Kein Text ausgewählt')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('ai.optimizationGoal', 'Optimierungsziel')}
                  </label>
                  <select
                    value={optimizationGoal}
                    onChange={(e) => setOptimizationGoal(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    <option value="clarity">{t('ai.goals.clarity', 'Klarheit')}</option>
                    <option value="engagement">{t('ai.goals.engagement', 'Engagement')}</option>
                    <option value="brevity">{t('ai.goals.brevity', 'Kürze')}</option>
                    <option value="impact">{t('ai.goals.impact', 'Wirkung')}</option>
                  </select>
                </div>

                <button
                  onClick={handleOptimize}
                  disabled={loading || !currentText}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {loading ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  {loading ? t('ai.optimizing', 'Optimiere...') : t('ai.optimize', 'Optimieren')}
                </button>
              </div>
            )}

            {/* ❌ DEAKTIVIERT - Template Library (Option C: Backend bleibt aktiv, Frontend hidden)
            Templates Tab - NEW Template Library Integration
            {activeTab === 'templates' && (
              <TemplateTab onTemplateSelect={handleTemplateLibrarySelect} />
            )}
            */}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-3">
                {history.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <History size={24} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('ai.noHistory', 'Noch keine Generierungen')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {history.map(item => (
                      <div
                        key={item.id}
                        className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <button
                            onClick={() => handleToggleFavorite(item.id)}
                            className={`mt-0.5 flex-shrink-0 ${item.favorite ? 'text-yellow-500' : 'text-gray-400'} hover:text-yellow-500 hover:scale-110 transition-all`}
                          >
                            <Star size={14} fill={item.favorite ? 'currentColor' : 'none'} />
                          </button>
                          <p className="flex-1 text-sm text-gray-900 dark:text-white line-clamp-2 leading-relaxed">
                            {item.content}
                          </p>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleCopy(item.content)}
                              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all hover:scale-105"
                              title="Kopieren"
                            >
                              {copySuccess === item.content ? <Check size={14} className="text-green-600" /> : <Copy size={14} className="text-gray-500" />}
                            </button>
                            <button
                              onClick={() => handleDeleteHistoryItem(item.id)}
                              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all hover:scale-105"
                              title="Löschen"
                            >
                              <Trash2 size={14} className="text-red-600 dark:text-red-400" />
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => handleApplyFromHistory(item.content)}
                          className="w-full px-3 py-2 text-sm font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 hover:shadow-md transition-all"
                        >
                          Anwenden
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Single Result */}
            {result && !batchMode && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-600 dark:text-green-400" />
                    <p className="text-xs font-medium text-green-700 dark:text-green-400">
                      {activeTab === 'generate' 
                        ? t('ai.generated', 'Generiert') 
                        : t('ai.optimized', 'Optimiert')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCopy(result.content || result.optimized)}
                    className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                  >
                    {copySuccess === (result.content || result.optimized) ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <p className="text-sm text-gray-900 dark:text-white mb-2">
                  {result.content || result.optimized}
                </p>
                <p className={`text-xs mb-2 ${getCharCountColor()}`}>
                  {countCharacters(result.content || result.optimized)} {t('ai.characters', 'Zeichen')}
                </p>
                <button
                  onClick={handleApply}
                  className="w-full py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                >
                  {t('ai.apply', 'Anwenden')}
                </button>
              </div>
            )}

            {/* Batch Results */}
            {batchResults.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Layers size={14} />
                  {batchResults.length} {t('ai.variants', 'Varianten')}
                </p>
                {batchResults.map((content, index) => (
                  <div 
                    key={index}
                    className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-xs font-medium text-green-700 dark:text-green-400">
                        #{index + 1}
                      </p>
                      <button
                        onClick={() => handleCopy(content)}
                        className="text-green-600 dark:text-green-400 hover:text-green-700 transition-colors"
                      >
                        {copySuccess === content ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-900 dark:text-white mb-1">
                      {content}
                    </p>
                    <p className={`text-xs mb-1 ${getCharCountColor()}`}>
                      {countCharacters(content)} {t('ai.characters', 'Zeichen')}
                    </p>
                    <button
                      onClick={() => handleApplyBatch(content)}
                      className="w-full py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                    >
                      {t('ai.apply', 'Anwenden')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistantEnhanced;